// ─────────────────────────────────────────────────────────────────────────────
// PrivacyTermsPage, the legal document set behind /privacy and /terms.
//
// One component serves both routes. The three documents live on the page in the
// same order they are argued about: whichever one the URL asked for comes first,
// the other two follow, and every heading keeps a stable id so the footer links
// (/privacy#privacy, /privacy#cookies, /terms#terms) always land in the right
// place. The SEO block follows the pathname too, so each route has its own
// title, description and self-referencing canonical.
//
// This is a long read rather than a pitch, so the design job here is legibility:
// a sticky index on the left that tracks where you are, a single column of prose
// on the right at a comfortable measure, numbered clause headings, hairlines
// between clauses and heavier hairlines between documents. Nothing is wrapped in
// a card, because a card around running prose only makes it harder to read.
//
// Everything factual in here matches what the product actually does: one card
// processor, no bank connection, no accounting connection, and a sub-processor
// list of five entries that a reviewer can check.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    NUMS,
    Icon,
    Container,
    Section,
    Heading,
    CardTitle,
    Lead,
    Pill,
    SectionHead,
    SpecList,
    PageHero,
    CTASection,
    MarketingShell,
    Seo,
    BTN_SECONDARY,
} from '../../components/marketing/MarketingKit';
import { Reveal } from '../../components/marketing/Motion';

const LAST_UPDATED = 'September 2, 2026';

// ── Document prose ───────────────────────────────────────────────────────────
// Each document is data: an id the footer can link to, a heading that ends in a
// full stop, and a list of clauses. A clause body is an array of paragraphs; an
// entry shaped { list: [...] } renders as a bulleted run instead.

const PRIVACY = {
    id: 'privacy',
    nav: 'Privacy notice',
    title: 'Privacy notice.',
    intro: 'What Wavelength collects when you use the public site, the dashboard and the apps that run on the floor, what we do with it, and who else touches it. Most of what we hold belongs to a business rather than to a person, and in that case the business decides what happens to it while we act on its instructions.',
    clauses: [
        {
            id: 'privacy-scope',
            nav: 'Who this covers',
            title: 'Who this covers',
            body: [
                'This notice applies to visitors to wavelengtherp.com, to the people who sign in to the Wavelength dashboard, and to the records a business connects to its workspace. Wavelength ERP is the company behind the service, and in this notice it is written as we or us.',
                <>
                    For the records inside a workspace, the business that owns the workspace decides what is collected and why, and we process it on that instruction. If you are a customer of a business that runs on Wavelength and you want your record corrected or removed, ask that business. We will pass the request on and act on what they tell us. The{' '}
                    <DocLink to="/dpa">data processing addendum</DocLink> sets that relationship out in full.
                </>,
            ],
        },
        {
            id: 'privacy-collect',
            nav: 'What we collect',
            title: 'What we collect',
            body: [
                'We collect what the service needs to work and stop there.',
                {
                    list: [
                        'Account details: your name, your work email address, the workspace and locations you belong to, your role, and a password held by our authentication provider in a form we cannot read.',
                        'Workspace records you connect or enter: orders and their line items, tenders and refunds, processor batches, drawer and safe counts, deposits, imported bank statement lines, inventory movements, purchase orders, receiving reports, production and label records, wholesale invoices and payments.',
                        'People inside those records: staff names, sign-in codes, per-app access and the locations a person works, together with the customer details that arrive with an order, such as a name, an email address, a phone number and a delivery address.',
                        'Device and usage data: which app and version a device is running, when it last checked in, the screens used, an IP address, timestamps, and error reports when something fails.',
                        'What you send us: support messages, and anything you attach to a message or to the in-app assistant.',
                    ],
                },
            ],
        },
        {
            id: 'privacy-sources',
            nav: 'Where it comes from',
            title: 'Where it comes from',
            body: [
                {
                    list: [
                        'Directly from you and your staff, in the dashboard and in the companion apps.',
                        'From the WordPress storefront you connect, which we read for orders and products.',
                        'From Datacap, where you use it as your card processor, for transactions and settlement batches.',
                        'From a bank statement file you upload. There is no live bank feed and no connection to your bank. Bank data reaches Wavelength only in a statement you export yourself and import.',
                        'From Google Analytics, for the traffic that reached the store you connect, matched against the orders it produced.',
                    ],
                },
            ],
        },
        {
            id: 'privacy-never',
            nav: 'What we never hold',
            title: 'What we never hold',
            body: [
                'We never see a full card number. The card is handled by the terminal and your processor. What comes back to Wavelength is the record of the transaction: the amount, the card brand, the last four digits, the approval and the batch it settled in. That is enough to reconcile a day and not enough to charge anybody.',
                'We do not build advertising profiles, we do not sell data, and we do not pass workspace records to anyone for their own marketing.',
                'The delivery app reports the position of a driver while a run is out, so the store and the customer can see where an order is. It stops when the run ends. It is not a record of where staff are for the rest of the day.',
            ],
        },
        {
            id: 'privacy-use',
            nav: 'How we use it',
            title: 'How we use it',
            body: [
                {
                    list: [
                        'To run the service: close the day per location, match register takings against processor batches and bank deposits, and produce the reporting on top of them.',
                        'To sign people in, enforce roles and locations, and keep the audit log of manual changes.',
                        'To answer support requests and send service and security notices.',
                        'To keep the platform secure, find faults and improve it.',
                        'To invoice you, and to meet legal, tax and accounting obligations.',
                    ],
                },
                'We do not build or train our own models on your workspace data.',
            ],
        },
        {
            id: 'privacy-share',
            nav: 'Who else touches it',
            title: 'Who else touches it',
            body: [
                'The list of sub-processors is short on purpose. These are the only companies that process workspace data on our behalf.',
                {
                    list: [
                        'Amazon Web Services: hosting, the database, file storage and sign-in. Everything else runs on top of it.',
                        'Google: Analytics for the traffic and campaign figures on the store you connect, and Maps for the delivery map.',
                        'Datacap: card transaction and settlement records, where you use Datacap as your processor.',
                        'OpenAI: the in-app assistant. When somebody asks a question, that question and the slice of the workspace needed to answer it are sent to the model. Nothing is sent until somebody asks.',
                        'The WordPress storefront you connect: your own system rather than one we chose. We read orders and products from it on your instruction.',
                    ],
                },
                'Accounting data leaves as a file rather than a connection. The QuickBooks export is a journal entry file you download and import yourself, so your accounting system is not joined to ours and Intuit receives nothing from us.',
                'We also disclose information where the law requires it, where it is needed to protect our rights or the safety of a person, and to a buyer if the company is ever sold, in which case we will tell you before anything moves.',
            ],
        },
        {
            id: 'privacy-security',
            nav: 'How it is protected',
            title: 'How it is protected',
            body: [
                'Data is encrypted in transit and at rest. Access is controlled per person across every module in the product, and every read and every write is scoped to a single workspace, so running more than one business from one login does not let either see the other. Staff sign-in codes are single use and expire. Keys and credentials stay on the server and are never shipped to the browser.',
                'The inventory ledger only appends, invoices are credited rather than deleted, and manual changes are written to a permanent audit log with the name of the person who made them. A correction is another entry, not an overwrite.',
                <>
                    We hold no SOC 2 report, no ISO certificate and no penetration test. The{' '}
                    <DocLink to="/security">security page</DocLink> says so plainly rather than showing a badge, and lists everything that is actually in place.
                </>,
            ],
        },
        {
            id: 'privacy-retention',
            nav: 'How long we keep it',
            title: 'How long we keep it',
            body: [
                'We keep account and workspace data for as long as the account is open and the service needs it. After an account ends we keep the data for a limited wind-down period so you can export it, then delete it, except where a longer period is required for legal, tax or accounting reasons. Backups age out on their own schedule.',
                'The audit log and the movement ledger are append-only by design, so the record of a correction stays for the retention period even after the value it corrected has changed.',
            ],
        },
        {
            id: 'privacy-rights',
            nav: 'Your rights',
            title: 'Your rights',
            body: [
                'Depending on where you live, you can ask for a copy of your personal information, ask us to correct or delete it, ask for it in a portable form, object to or restrict certain processing, and withdraw consent where we relied on it. Write to the address below and we will answer inside the period the law allows. A reasonable request costs nothing, and exercising a right never costs you service.',
                'Where the data belongs to a workspace we run for a business, we will route the request to that business, tell you we have done it, and act on their instruction.',
            ],
        },
        {
            id: 'privacy-transfers',
            nav: 'Where it is processed',
            title: 'Where it is processed',
            body: [
                'Wavelength is operated from the United States and the platform runs on infrastructure in the United States. The sub-processors listed above operate there too. Where a transfer out of your own region needs a legal mechanism, we rely on standard contractual clauses together with the measures described in the data processing addendum.',
            ],
        },
        {
            id: 'privacy-children',
            nav: 'Children',
            title: 'Children',
            body: [
                'The service is sold to businesses and is not directed at children under 16. We do not knowingly collect personal information from a child. If you believe a child has given us information, write to us and we will delete it.',
            ],
        },
        {
            id: 'privacy-changes',
            nav: 'Changes',
            title: 'Changes to this notice',
            body: [
                'We update this notice as the product changes. The date at the top of the page moves when it does, and where a change is material we will say so in the product or by email before it takes effect. Ask us and we will send you the previous version.',
            ],
        },
        {
            id: 'privacy-contact',
            nav: 'Contact',
            title: 'Contact',
            body: [
                <>Privacy questions and requests go to <MailLink address="privacy@wavelengtherp.com" />, and a person answers them.</>,
            ],
        },
    ],
};

const COOKIES = {
    id: 'cookies',
    nav: 'Cookie notice',
    title: 'Cookie notice.',
    intro: 'Wavelength sets a small number of cookies and uses a fair amount of browser storage. This is the whole list.',
    clauses: [
        {
            id: 'cookies-what',
            nav: 'What we set',
            title: 'What we set',
            body: [
                {
                    list: [
                        'Sign-in: the session tokens that keep you signed in and are refreshed in the background. Clearing them signs you out.',
                        'Preferences: the workspace and location you last chose, the light or dark theme, the state of the sidebar, and how you last sorted a table. Most of this is browser storage rather than a cookie, and it stays on your device.',
                        'Measurement: Google Analytics on the public site, so we can see which pages get read and which do not.',
                    ],
                },
            ],
        },
        {
            id: 'cookies-not',
            nav: 'What we do not set',
            title: 'What we do not set',
            body: [
                'No advertising cookies, no retargeting pixels and no third-party ad network. We do not sell or share visitor data for advertising, and nothing you do inside the dashboard is measured for it.',
            ],
        },
        {
            id: 'cookies-managing',
            nav: 'Managing them',
            title: 'Managing them',
            body: [
                'Every browser can list, block and delete cookies, and yours will explain how. Blocking the essential ones means you cannot sign in, because the session is the cookie. Where the law that applies to you requires consent before anything non-essential is set, we ask first.',
            ],
        },
        {
            id: 'cookies-dnt',
            nav: 'Do Not Track',
            title: 'Do Not Track',
            body: [
                'Some browsers send a Do Not Track signal. There is still no agreed standard for what a site should do with it, so we do not change our behaviour based on it. If that changes, this section changes with it.',
            ],
        },
    ],
};

const TERMS = {
    id: 'terms',
    nav: 'Terms of service',
    title: 'Terms of service.',
    intro: 'These terms govern your use of Wavelength. By creating an account or using the service you accept them, and if you are doing that for a business you confirm you are allowed to commit that business.',
    clauses: [
        {
            id: 'terms-service',
            nav: 'What Wavelength is',
            title: 'What Wavelength is',
            body: [
                'Wavelength is a back office. It sits alongside the point of sale each of your stores already runs rather than replacing it. It closes the day per location, controls the cash with blind counts and sign-off, matches register takings against processor batches and bank deposits, and reports on what sold. Companion apps cover the register, the scale, packing, receiving and delivery.',
                'We add, change and remove features as the product develops. If we remove something you rely on, we will tell you before it goes.',
            ],
        },
        {
            id: 'terms-accounts',
            nav: 'Your account',
            title: 'Your account',
            body: [
                'You are responsible for your account and for what happens under it. Keep credentials confidential, assign roles deliberately, and treat the staff codes issued inside the product as keys to your own money. Tell us promptly if you think somebody has access who should not.',
                'You are responsible for the accuracy of what you enter and what you connect. We reconcile the records we are given; we cannot verify that they describe what happened on the floor.',
            ],
        },
        {
            id: 'terms-acceptable',
            nav: 'Acceptable use',
            title: 'Acceptable use',
            body: [
                'You agree not to:',
                {
                    list: [
                        'Use the service to break the law or infringe the rights of anybody else.',
                        'Probe, scan or attempt to breach the security of the service or of another workspace.',
                        'Reverse engineer, resell or copy the service, except where the law says you may.',
                        'Upload malware, or interfere with the service for other users.',
                        'Store data in the service that you have no right to hold.',
                    ],
                },
            ],
        },
        {
            id: 'terms-data',
            nav: 'Your data stays yours',
            title: 'Your data stays yours',
            body: [
                'You own the records you put into Wavelength. You grant us a limited licence to host, process and display them so we can run the service for you, support you and keep it secure. We do not sell your data, we do not use it for advertising, and we do not build or train our own models on it.',
                'Every table exports to CSV, Excel or a branded PDF for as long as your account is open, and the export is the same data you are looking at rather than a summary of it.',
            ],
        },
        {
            id: 'terms-not',
            nav: 'What it is not',
            title: 'What it is not',
            body: [
                'Wavelength is not your accounting system and does not try to be. The QuickBooks export is a journal entry file for your bookkeeper to import, and the numbers still need a person who signs them.',
                'Reconciliation tells you what does not match. It does not tell you why, and it does not replace your own review. You remain responsible for the figures you file, publish or pay from.',
                'Wavelength does not track cost of goods, margin or profitability, so nothing in the product should be read as a statement of what a location earned.',
            ],
        },
        {
            id: 'terms-money',
            nav: 'Money',
            title: 'Money',
            body: [
                'We never hold your money. Where the register takes a card payment it goes to your processor on your own merchant account, and settlement runs from your processor to your bank without passing through us. Your agreements with your processor and your bank are yours, and so are their fees.',
                'Bank data reaches us only in a statement file you upload. We have no ability to move funds, initiate a transfer or change anything at your bank.',
            ],
        },
        {
            id: 'terms-assistant',
            nav: 'Assistant and drafts',
            title: 'Assistant and drafts',
            body: [
                'The in-app assistant answers from your own records and shows the numbers it used. It can still be wrong, and an answer is not advice. Check anything you are about to act on.',
                'The order agent turns a message or a photograph into a draft order at your own prices. A draft is a draft. Somebody has to read it and confirm it before it becomes an order.',
            ],
        },
        {
            id: 'terms-third-party',
            nav: 'Third-party services',
            title: 'Third-party services',
            body: [
                'The service reads from systems you choose, such as your storefront, your card processor and your analytics property. Those relationships are yours and their own terms govern them. If one of them changes, breaks or shuts down, the features that depend on it are affected, and that is not a breach of these terms.',
            ],
        },
        {
            id: 'terms-fees',
            nav: 'Fees and billing',
            title: 'Fees and billing',
            body: [
                <>
                    Wavelength is a subscription priced per location, published on the{' '}
                    <DocLink to="/pricing">pricing page</DocLink>. Plans are set up with you rather than bought through a checkout, and we invoice in advance for the period you choose. Fees exclude taxes, which are yours.
                </>,
                'Fees are not refundable except where the law requires it. We give reasonable notice before a price changes, and a change never applies to a period you have already paid for.',
                'If an invoice goes unpaid we will chase it before we do anything else, and we will tell you before an account is suspended.',
            ],
        },
        {
            id: 'terms-availability',
            nav: 'Availability and support',
            title: 'Availability and support',
            body: [
                'We do not publish an uptime figure and these terms carry no service level credit. We will give notice of planned maintenance where we can, and the status page shows the current state of the platform.',
                'Support is by email, answered by the people who build the product.',
            ],
        },
        {
            id: 'terms-ip',
            nav: 'Intellectual property',
            title: 'Intellectual property',
            body: [
                'The service, including its software, design and content other than your own data, belongs to Wavelength and is protected by intellectual property law. You get a limited, non-transferable right to use it for the length of your subscription. If you send us feedback we may act on it without owing you anything for it. All rights not granted here are reserved.',
            ],
        },
        {
            id: 'terms-disclaimers',
            nav: 'Disclaimers',
            title: 'Disclaimers',
            body: [
                'The service is provided as is and as available, without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose and non-infringement. We do not warrant that the service will be uninterrupted or free of faults, or that reconciliation will surface every discrepancy in your business.',
            ],
        },
        {
            id: 'terms-liability',
            nav: 'Limitation of liability',
            title: 'Limitation of liability',
            body: [
                'To the maximum extent the law allows, Wavelength is not liable for indirect, incidental, special, consequential or punitive damages, or for lost profits, revenue, goodwill or data. Our total liability for any claim relating to the service is limited to the fees you paid us in the twelve months before the event that gave rise to it. Some jurisdictions do not allow parts of this limitation, and in those places it applies only as far as it is permitted.',
            ],
        },
        {
            id: 'terms-indemnity',
            nav: 'Indemnity',
            title: 'Indemnity',
            body: [
                'You agree to cover Wavelength against claims, losses and reasonable legal costs arising from your use of the service, from the data you put into it, or from a breach of these terms by you or by somebody using your account.',
            ],
        },
        {
            id: 'terms-termination',
            nav: 'Term and termination',
            title: 'Term and termination',
            body: [
                'These terms apply for as long as you use the service. You can cancel at any time, effective at the end of the period you have paid for. We can suspend or end access for a breach of these terms, for non-payment, or for use that puts other customers at risk, and outside an emergency we will warn you first.',
                'When an account ends, the right to use the service ends with it. We keep your data available for export for a limited period, then delete it as described in the privacy notice.',
            ],
        },
        {
            id: 'terms-law',
            nav: 'Governing law',
            title: 'Governing law',
            body: [
                'These terms are governed by the laws of the state in which Wavelength is organised, without regard to its conflict of laws rules. For disputes that are not subject to arbitration, you agree to the exclusive jurisdiction of the courts located there.',
            ],
        },
        {
            id: 'terms-changes',
            nav: 'Changes',
            title: 'Changes to these terms',
            body: [
                'We update these terms as the product and the law change. The date at the top of the page moves when they do, and where a change is material we will tell you in the product or by email before it takes effect. Continuing to use the service after that is acceptance; if you would rather not, you can cancel.',
            ],
        },
        {
            id: 'terms-contact',
            nav: 'Contact',
            title: 'Contact',
            body: [
                <>Questions about these terms go to <MailLink address="legal@wavelengtherp.com" />.</>,
            ],
        },
    ],
};

// ── Local prose primitives ───────────────────────────────────────────────────
// The kit has no document type, so these three are defined here: 15px on a 620px
// measure, which is the width a long clause stays readable at. Nothing is boxed.

function DocLink({ to, children }) {
    return (
        <Link to={to} className="font-medium text-[#0A0A0A] underline decoration-[rgba(10,10,10,0.30)] underline-offset-[3px] transition-colors hover:decoration-[#0A0A0A]">
            {children}
        </Link>
    );
}

function MailLink({ address }) {
    return (
        <a href={`mailto:${address}`} className="font-medium text-[#0A0A0A] underline decoration-[rgba(10,10,10,0.30)] underline-offset-[3px] transition-colors hover:decoration-[#0A0A0A]">
            {address}
        </a>
    );
}

function P({ children }) {
    return <p className="mt-4 text-[15px] leading-[1.75] text-[#6B6B6B]">{children}</p>;
}

// A bulleted run, marked with a hairline dash rather than a disc so the list
// keeps the same monochrome hairline language as everything else on the site.
function Bullets({ items }) {
    return (
        <ul className="mt-4 flex flex-col gap-2.5">
            {items.map((it, i) => (
                <li key={i} className="flex gap-3 text-[15px] leading-[1.75] text-[#6B6B6B]">
                    <span className="mt-[13px] h-px w-3 flex-none bg-[rgba(10,10,10,0.25)]" />
                    <span>{it}</span>
                </li>
            ))}
        </ul>
    );
}

function Prose({ items }) {
    return items.map((item, i) => (
        item && item.list ? <Bullets key={i} items={item.list} /> : <P key={i}>{item}</P>
    ));
}

// ── The index rail ───────────────────────────────────────────────────────────
// Tracks the clause currently under the top of the viewport, so a long document
// always says where you are. Falls back to the first clause without an observer.
function useActiveId(ids) {
    const [active, setActive] = useState(ids[0]);
    useEffect(() => {
        if (typeof IntersectionObserver !== 'function') return undefined;
        const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
        if (!els.length) return undefined;
        const visible = new Map();
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => visible.set(e.target.id, e.isIntersecting));
                const first = ids.find((id) => visible.get(id));
                if (first) setActive(first);
            },
            { rootMargin: '-104px 0px -68% 0px', threshold: 0 },
        );
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, [ids]);
    return active;
}

function TocLink({ id, active, sub = false, children }) {
    const tone = active
        ? 'bg-[#F5F5F5] text-[#0A0A0A]'
        : sub ? 'text-[#A1A1A1] hover:text-[#0A0A0A]' : 'text-[#0A0A0A]';
    return (
        <a href={`#${id}`} className={`block rounded-full px-3 py-1.5 text-[13px] transition-colors ${sub ? '' : 'font-medium'} ${tone}`}>
            {children}
        </a>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function PrivacyTermsPage() {
    const { pathname } = useLocation();
    const isTerms = pathname.startsWith('/terms');

    // Whichever document the URL asked for leads. The other two follow in the
    // order they are usually read after it.
    const docs = useMemo(() => (isTerms ? [TERMS, PRIVACY, COOKIES] : [PRIVACY, COOKIES, TERMS]), [isTerms]);
    const ids = useMemo(() => docs.flatMap((d) => [d.id, ...d.clauses.map((c) => c.id)]), [docs]);
    const active = useActiveId(ids);

    const seo = isTerms
        ? {
            title: 'Terms of service',
            description: 'The terms of service for Wavelength: what the back office does, who owns the data in it, how plans are billed per location, the liability limits, how it ends.',
            keywords: 'Wavelength terms of service, retail back office terms, SaaS subscription terms, customer data ownership, limitation of liability, acceptable use policy, account termination',
            crumb: 'Terms of service',
        }
        : {
            title: 'Privacy notice',
            description: 'How Wavelength collects, uses and protects the data in your workspace: what we hold, the sub-processors that touch it, how long we keep it and your rights.',
            keywords: 'Wavelength privacy notice, retail software privacy policy, sub-processor list, cookie notice, data retention, GDPR rights, CCPA rights, back office data protection',
            crumb: 'Privacy notice',
        };

    return (
        <MarketingShell>
            <Seo
                title={seo.title}
                description={seo.description}
                path={pathname}
                keywords={seo.keywords}
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@graph': [
                        {
                            '@type': 'WebPage',
                            '@id': `https://wavelengtherp.com${pathname}#webpage`,
                            url: `https://wavelengtherp.com${pathname}`,
                            name: seo.title,
                            description: seo.description,
                            dateModified: '2026-09-02',
                            publisher: { '@id': 'https://wavelengtherp.com/#organization' },
                        },
                        {
                            '@type': 'BreadcrumbList',
                            itemListElement: [
                                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://wavelengtherp.com/' },
                                { '@type': 'ListItem', position: 2, name: 'Legal', item: 'https://wavelengtherp.com/privacy' },
                                { '@type': 'ListItem', position: 3, name: seo.crumb, item: `https://wavelengtherp.com${pathname}` },
                            ],
                        },
                    ],
                }}
            />

            {/* ── Hero ───────────────────────────────────────────────────── */}
            <PageHero
                eyebrow="Legal"
                title={isTerms ? 'The rules for using Wavelength.' : 'How we handle your data.'}
                lead={
                    isTerms
                        ? 'What you are agreeing to, what we are responsible for, and what happens to your records if you leave. The privacy and cookie notices sit on the same page, because nobody should have to read three documents in three places.'
                        : 'What we hold, who else touches it, how long it stays and what you can ask us to do with it. The terms of service and the cookie notice sit on the same page, written the same way.'
                }
            >
                <div className="flex flex-col gap-5 border-t border-[rgba(10,10,10,0.10)] pt-6">
                    <div className="text-[13px] text-[#A1A1A1]">Last updated {LAST_UPDATED}. All three documents on this page carry the same date.</div>
                    <div className="flex flex-wrap gap-2">
                        <Pill as="a" href="#privacy" active={!isTerms}>Privacy notice</Pill>
                        <Pill as="a" href="#cookies">Cookie notice</Pill>
                        <Pill as="a" href="#terms" active={isTerms}>Terms of service</Pill>
                        <Pill as={Link} to="/dpa">Data processing addendum</Pill>
                    </div>
                </div>
            </PageHero>

            {/* ── Index rail plus the documents ──────────────────────────── */}
            <Section pad="md">
                <Container>
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[200px_1fr] lg:gap-16">
                        <nav aria-label="On this page" className="hidden lg:block">
                            {/* The full index is longer than a viewport, so the rail
                                scrolls inside itself rather than stranding the last
                                clause below the fold. */}
                            <div className="sticky top-24 flex max-h-[calc(100vh-7rem)] flex-col overflow-y-auto pb-4 [scrollbar-width:thin]">
                                <div className="px-3 text-[12px] font-medium uppercase tracking-[0.14em] text-[#A1A1A1]">On this page</div>
                                <div className="mt-4 flex flex-col gap-0.5">
                                    {docs.map((doc, i) => (
                                        <div key={doc.id} className={i > 0 ? 'mt-3 border-t border-[rgba(10,10,10,0.08)] pt-3' : ''}>
                                            <TocLink id={doc.id} active={active === doc.id}>{doc.nav}</TocLink>
                                            {doc.clauses.map((c) => (
                                                <TocLink key={c.id} id={c.id} sub active={active === c.id}>{c.nav}</TocLink>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-4 border-t border-[rgba(10,10,10,0.08)] px-3 pt-4">
                                    <Link to="/dpa" className="text-[13px] text-[#A1A1A1] transition-colors hover:text-[#0A0A0A]">Data processing addendum</Link>
                                </div>
                            </div>
                        </nav>

                        <div className="min-w-0 max-w-[620px]">
                            {docs.map((doc, i) => (
                                <article
                                    key={doc.id}
                                    className={i > 0 ? 'mt-20 border-t border-[rgba(10,10,10,0.10)] pt-16' : ''}
                                >
                                    <div id={doc.id} className="scroll-mt-28">
                                        <Heading>{doc.title}</Heading>
                                    </div>
                                    <Lead className="mt-5">{doc.intro}</Lead>

                                    {doc.clauses.map((c, n) => (
                                        <section
                                            key={c.id}
                                            id={c.id}
                                            className="mt-10 scroll-mt-28 border-t border-[rgba(10,10,10,0.08)] pt-10"
                                        >
                                            <div className={`text-[13px] font-medium text-[#A1A1A1] ${NUMS}`}>
                                                {String(n + 1).padStart(2, '0')}
                                            </div>
                                            <CardTitle className="mt-2">{c.title}</CardTitle>
                                            <Prose items={c.body} />
                                        </section>
                                    ))}
                                </article>
                            ))}
                        </div>
                    </div>
                </Container>
            </Section>

            {/* ── Where to write ─────────────────────────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[340px_1fr] lg:gap-16">
                        <Reveal>
                            <div className="flex flex-col gap-4">
                                <SectionHead
                                    title="Where to write."
                                    lead="Legal pages usually end at a form. These end at two addresses that a person reads, and at the addendum your reviewer will ask for next."
                                    maxWidth="max-w-[420px]"
                                />
                                <div className="mt-2">
                                    <Link to="/dpa" className={BTN_SECONDARY}>
                                        Read the addendum
                                        <Icon name="arrow" className="h-4 w-4" />
                                    </Link>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={80}>
                            <SpecList
                                rows={[
                                    { label: 'Privacy requests and questions', value: <MailLink address="privacy@wavelengtherp.com" /> },
                                    { label: 'Terms, contracts and procurement', value: <MailLink address="legal@wavelengtherp.com" /> },
                                    { label: 'Security review', value: <DocLink to="/security">Security and access control</DocLink> },
                                    { label: 'Processing terms', value: <DocLink to="/dpa">Data processing addendum</DocLink> },
                                    { label: 'Everything else', value: <DocLink to="/contact">Talk to us</DocLink> },
                                ]}
                            />
                        </Reveal>
                    </div>
                </Container>
            </Section>

            {/* ── Closing CTA ────────────────────────────────────────────── */}
            <CTASection
                eyebrow="Legal"
                title="Ask a person, not a portal."
                body="If your review needs something these pages do not answer, write to us. Somebody who works on the product will reply, including with the answers that are still no."
                primary={{ label: 'Talk to us', to: '/contact' }}
                secondary={{ label: 'How it is built', to: '/security' }}
            />
        </MarketingShell>
    );
}
